// Dogecoin — derivation, validation, UTXO planning and signing.
// Browser-safe: pure @scure/noble stack, no WASM, no node crypto.
//
// Deliberately shaped like ../btc, down to the function names, because it is
// the same problem: select outputs, return change, sign. Coin selection is the
// same audited selectUTXO, so the fee shown is the fee paid here too.
//
// Three things do NOT carry over from Bitcoin, and each is a quiet way to lose
// money:
//
//   1. Addresses are base58check P2PKH, never bech32 — its own network
//      constant, and BIP-44 rather than BIP-84.
//   2. Signing a legacy input needs the WHOLE previous transaction. A P2WPKH
//      input commits to its amount in the sighash, so Bitcoin can be handed
//      just a script and a value; a P2PKH input commits to neither, so the
//      signer has to verify against the funding transaction itself.
//   3. The dust limit is 1,000,000 koinu against Bitcoin's 546 satoshi. Change
//      Bitcoin would happily create is unspendable here.

import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync } from '@scure/bip39';
import { hex } from '@scure/base';
import * as btc from '@scure/btc-signer';
import { DOGE_DUST_KOINU, DOGE_FEE_PER_KB_KOINU, dogeFeeKoinu } from '@incpt/shared';
import type { DerivedAddresses } from '../types';

/**
 * Dogecoin mainnet version bytes.
 *
 * `bech32` is required by the type but unreachable: the chain has no SegWit,
 * so nothing here calls p2wpkh. It carries the registered HRP rather than
 * Bitcoin's 'bc' so that a future caller which does reach it fails visibly
 * instead of producing a Bitcoin address.
 */
export const DOGE_NETWORK: typeof btc.NETWORK = {
  bech32: 'doge',
  pubKeyHash: 0x1e, // D…
  scriptHash: 0x16, // A… / 9…
  wif: 0x9e,
};

/** BIP-44: m/44'/3'/0'/0/index → P2PKH (D…). */
const PATH = (index: number) => `m/44'/3'/0'/0/${index}`;

export { DOGE_DUST_KOINU };

/** D… address + compressed pubkey at `index` (mirrors deriveBtcAddresses). */
export function deriveDogeAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.publicKey) throw new Error('doge derivation failed: no public key');
  const pay = btc.p2pkh(child.publicKey, DOGE_NETWORK);
  if (!pay.address) throw new Error('doge derivation failed: no address');
  return { eoa: pay.address, publicKey: hex.encode(child.publicKey) };
}

function dogeChild(mnemonic: string, index: number) {
  const child = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive(PATH(index));
  if (!child.privateKey || !child.publicKey) throw new Error('doge derivation failed');
  return { priv: child.privateKey, pub: child.publicKey };
}

export function dogeAddressFromMnemonic(mnemonic: string, index = 0): string {
  return deriveDogeAddresses(mnemonicToSeedSync(mnemonic), index).eoa;
}

/**
 * Well-formed MAINNET Dogecoin address.
 *
 * Decoded, not pattern-matched. 'D' is the usual first character but that is a
 * consequence of the 0x1e version byte, not a rule: P2SH starts 'A' or '9'. A
 * regex anchored on 'D' would reject a legitimate multisig recipient while
 * waving through a mistyped string of the right shape.
 */
export function isValidDogeAddress(address: string): boolean {
  try {
    const decoded = btc.Address(DOGE_NETWORK).decode(address);
    return decoded?.type === 'pkh' || decoded?.type === 'sh';
  } catch {
    return false;
  }
}

/** One spendable output. `prevTxHex` is the full funding transaction — a
 *  legacy input cannot be signed without it (see the note at the top). */
export interface DogeUtxo {
  txid: string;
  vout: number;
  valueKoinu: bigint;
  prevTxHex: string;
}

export interface DogeTxPlan {
  inputCount: number;
  feeKoinu: bigint;
  /** Whether a change output was created (sub-dust change folds into the fee). */
  hasChange: boolean;
  /** Total koinu leaving the wallet: amount + fee. */
  totalKoinu: bigint;
}

function toSelectInputs(utxos: DogeUtxo[], publicKey: Uint8Array) {
  const pay = btc.p2pkh(publicKey, DOGE_NETWORK);
  return utxos.map((u) => ({
    ...pay,
    txid: hex.decode(u.txid),
    index: u.vout,
    nonWitnessUtxo: hex.decode(u.prevTxHex),
  }));
}

function runSelection(p: {
  utxos: DogeUtxo[];
  to: string;
  amountKoinu: bigint;
  feePerKbKoinu: bigint;
  changeAddress: string;
  publicKey: Uint8Array;
  createTx: boolean;
}) {
  // selectUTXO works in koinu per byte; dogeFeeKoinu owns the per-kB rate and
  // the relay floor, so ask it what one kilobyte costs rather than dividing
  // here and letting the two drift.
  const feePerByte = dogeFeeKoinu(1000, p.feePerKbKoinu) / 1000n;
  const selected = btc.selectUTXO(
    toSelectInputs(p.utxos, p.publicKey),
    [{ address: p.to, amount: p.amountKoinu }],
    'default',
    {
      changeAddress: p.changeAddress,
      feePerByte: feePerByte > 0n ? feePerByte : 1n,
      bip69: false,
      createTx: p.createTx,
      network: DOGE_NETWORK,
      dust: DOGE_DUST_KOINU,
      allowUnknownOutputs: false,
    },
  );
  if (!selected || selected.fee === undefined) throw new Error('insufficient_funds');
  return { ...selected, fee: selected.fee };
}

/**
 * Pure planner for the fee display — needs only the PUBLIC key (available in
 * wallet.addresses.publicKey), so a quote never unlocks the seed.
 * Throws Error('insufficient_funds') when the UTXOs can't cover amount + fee.
 */
export function planDogeTx(p: {
  utxos: DogeUtxo[];
  to: string;
  amountKoinu: bigint;
  changeAddress: string;
  publicKeyHex: string;
  feePerKbKoinu?: bigint;
}): DogeTxPlan {
  const selected = runSelection({
    ...p,
    feePerKbKoinu: p.feePerKbKoinu ?? DOGE_FEE_PER_KB_KOINU,
    publicKey: hex.decode(p.publicKeyHex),
    createTx: false,
  });
  return {
    inputCount: selected.inputs.length,
    feeKoinu: selected.fee,
    hasChange: selected.change,
    totalKoinu: p.amountKoinu + selected.fee,
  };
}

/**
 * Largest amount (koinu) sendable from the given UTXOs.
 * Same selection path as the real send, so Max never overquotes.
 */
export function planDogeMaxSend(p: {
  utxos: DogeUtxo[];
  to: string;
  changeAddress: string;
  publicKeyHex: string;
  feePerKbKoinu?: bigint;
}): { maxKoinu: bigint; feeKoinu: bigint } {
  const total = p.utxos.reduce((s, u) => s + u.valueKoinu, 0n);
  if (total === 0n || p.utxos.length === 0) return { maxKoinu: 0n, feeKoinu: 0n };

  let lo = 0n;
  let hi = total;
  let best = 0n;
  let bestFee = 0n;
  while (lo <= hi) {
    const mid = (lo + hi) / 2n;
    if (mid <= 0n) {
      lo = 1n;
      continue;
    }
    try {
      const plan = planDogeTx({ ...p, amountKoinu: mid });
      best = mid;
      bestFee = plan.feeKoinu;
      lo = mid + 1n;
    } catch {
      hi = mid - 1n;
    }
  }
  return { maxKoinu: best, feeKoinu: bestFee };
}

export interface SignedDogeTx {
  hex: string;
  txid: string;
  feeKoinu: bigint;
  bytes: number;
}

/** Build + sign a P2PKH spend. Same selection as planDogeTx (createTx: true),
 *  then sign every input and finalize. Default sequence — no RBF, matching
 *  Bitcoin here: there is no fee-bump UI to make it useful. */
export function buildAndSignDogeTx(p: {
  utxos: DogeUtxo[];
  to: string;
  amountKoinu: bigint;
  changeAddress: string;
  mnemonic: string;
  index?: number;
  feePerKbKoinu?: bigint;
}): SignedDogeTx {
  const { priv, pub } = dogeChild(p.mnemonic, p.index ?? 0);
  const selected = runSelection({
    utxos: p.utxos,
    to: p.to,
    amountKoinu: p.amountKoinu,
    feePerKbKoinu: p.feePerKbKoinu ?? DOGE_FEE_PER_KB_KOINU,
    changeAddress: p.changeAddress,
    publicKey: pub,
    createTx: true,
  });
  const tx = selected.tx;
  if (!tx) throw new Error('doge tx build failed');
  tx.sign(priv);
  tx.finalize();
  // vsize equals size on a chain with no witness discount, but read the real
  // number rather than assuming — the fee was computed from the same estimate.
  return { hex: tx.hex, txid: tx.id, feeKoinu: selected.fee, bytes: tx.vsize };
}
